import globalLess from '@/global.less'
import { ReactComponent as DeleteIcon } from '@/assets/trash_bin.svg'
import CreateCourseDialog from '@/components/CreateCourseDialog'
import CreateCourseTemplateCategoryDialog from '@/components/CreateCourseTemplateCategoryDialog'
import EditCategoryVideoDialog from '@/components/EditCategoryVideoDialog'
import EditCourseTemplateCategoryDialog from '@/components/EditCourseTemplateCategoryDialog'
import EditVideoCategoryDialog from '@/components/EditVideoCategoryDialog'
import LabelSelectList from '@/components/LabelSelectList'
import PermissionsController from '@/components/PermissionsController'
import PreviewCourseVideoDialog from '@/components/PreviewCourseVideoDialog'
import VideoPreviewDialog from '@/components/VideoPreviewDialog'
import usePermissions from '@/hooks/usePermissions'
import durationToStr from '@/libs/utils/durationToStr'
import Button from '@/shared/Button'
import ConfirmDialog from '@/shared/ConfirmDialog'
import FormCreator from '@/shared/FormCreator'
import AddIcon from '@mui/icons-material/Add'
import AddCircle from '@mui/icons-material/AddCircle'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import Edit from '@mui/icons-material/Edit'
import PlaylistAddOutlined from '@mui/icons-material/PlaylistAddOutlined'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Container from '@mui/material/Container'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import debounce from '@mui/material/utils/debounce'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Carousel from 'react-material-ui-carousel'
import { connect } from 'react-redux'

const LIST_PAGE_SIZE = 5

const mapStateToProps = ({ loading, courseTemplate }) => ({
  courseTemplateList: courseTemplate.courseTemplateList,
  courseTemplateLabels: courseTemplate.courseTemplateLabels,
  isLoading: loading['courseTemplate/getCourseTemplates'],
  isLabelsLoading: loading['courseTemplate/getCourseTemplateLabels'],
  deleteLoading:
    loading['courseTemplate/deleteCourseTemplate'] ||
    loading['courseTemplate/deleteCourseTemplateFromLabel'],
})

const CourseManagement = ({
  dispatch,
  courseTemplateList,
  courseTemplateLabels,
  isLoading,
  isLabelsLoading,
  deleteLoading,
}) => {
  const { data, paging = {} } = courseTemplateList || {}
  const { data: labelData, paging: labelPaging = {} } = courseTemplateLabels

  const { t } = useTranslation()
  const [page, setPage] = useState(0)
  const labelPage = useRef(0)
  const labelOptionsName = useRef()
  const [deleteLabelData, setDeleteLabelData] = useState()
  const [deleteCourseLabelData, setDeleteCourseLabelData] = useState()
  const [editLabelData, setEditLabelData] = useState()
  const [createLabelDialogOpen, setCreateLabelDialogOpen] = useState(false)
  const [createCourseDialogOpen, setCreateCourseDialogOpen] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState([])
  const [editVideoCategoryData, setEditVideoCategoryData] = useState()
  const [editCategoryVideoData, setEditCategoryVideoData] = useState()
  const [previewCourseData, setPreviewCourseData] = useState()
  const [videoPreviewData, setVideoPreviewData] = useState()

  const labelOptionsPage = useRef(0)
  const [labelOptions, setLabelOptions] = useState([])

  const canCreate = usePermissions('courseVideo_create')
  const canModify = usePermissions('courseVideo_modify')

  const form = useRef()

  const getLabelOptions = () => {
    dispatch({
      type: 'courseTemplate/getCourseTemplateLabels',
      payload: {
        pageIndex: labelOptionsPage.current,
        pageSize: 10,
        searchLabelName: labelOptionsName.current,
      },
      skipPut: true,
      successCallback: response => {
        const { data: responseData } = response

        const transformedOptions = responseData.map(({ labelId, name }) => ({
          label: name,
          id: labelId,
        }))

        if (transformedOptions) {
          setLabelOptions(transformedOptions)
        }
      },
    })
  }

  const setLabelOptionsNameDebounce = debounce(value => {
    labelOptionsName.current = value

    if (!isLabelsLoading) {
      getLabelOptions()
    }
  }, 400)

  const fetchData = useCallback(() => {
    const formValues = form.current?.getValues()
    const { search } = formValues || {}

    const payload = {
      searchName: search,
      searchLabelName: labelOptionsName.current,
      labelId: selectedLabel,
      pageIndex: page,
      pageSize: LIST_PAGE_SIZE,
    }

    dispatch({
      type: 'courseTemplate/getCourseTemplates',
      payload,
    })
  }, [dispatch, page, form, selectedLabel])

  const getLabels = () => {
    const formValues = form.current?.getValues()
    const { search: searchCourseName } = formValues || {}

    dispatch({
      type: 'courseTemplate/getCourseTemplateLabels',
      payload: {
        pageIndex: labelPage.current,
        pageSize: 10,
        searchLabelName: labelOptionsName.current,
        searchCourseName,
      },
    })
  }

  const deleteCourseTemplateLabel = () => {
    if (deleteLabelData) {
      const { labelId, name } = deleteLabelData

      dispatch({
        type: 'courseTemplate/deleteCourseTemplateLabel',
        payload: { labelId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.category_deleted', {
                name,
              }),
              severity: 'success',
            },
          })
          setDeleteLabelData()
          getLabels()
        },
      })
    }
  }

  const deleteCourseTemplateFromLabel = () => {
    if (deleteCourseLabelData) {
      const { label, course } = deleteCourseLabelData

      dispatch({
        type: 'courseTemplate/deleteCourseTemplateFromLabel',
        payload: { labelId: label?.labelId, courseTempId: course?.templateId },
        successCallback: () => {
          dispatch({
            type: 'app/notify',
            payload: {
              message: t('messages.course_from_label_deleted', {
                category: label?.name,
                course: course?.name,
              }),
              severity: 'success',
            },
          })
          setDeleteCourseLabelData()
          getLabels()
          fetchData()
        },
      })
    }
  }

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedLabel])

  useEffect(() => {
    getLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const isEmptyData = data?.length === 0 && !isLoading

  return (
    <Container maxWidth={false} sx={{ flex: 1, display: 'flex' }}>
      <Grid container spacing={2} flexDirection="column">
        <Grid item>
          <Grid container>
            <Grid item>
              <FormCreator
                ref={form}
                formItems={[
                  ...(canCreate
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<PlaylistAddOutlined sx={{ color: '#2D2D6C80' }} />}
                              onClick={() => {
                                setCreateCourseDialogOpen(true)
                              }}
                            >
                              {t('general.upload_video')}
                            </Button>
                          ),
                          name: 'upload_video',
                        },
                      ]
                    : []),
                  ...(canCreate
                    ? [
                        {
                          component: () => (
                            <Button
                              variant="text"
                              color="secondary_80"
                              endIcon={<AddCircle />}
                              onClick={() => {
                                setCreateLabelDialogOpen(true)
                              }}
                            >
                              {t('general.add_category')}
                            </Button>
                          ),
                          name: 'add_category',
                        },
                      ]
                    : []),
                  {
                    name: 'search_label',
                    type: 'autocomplete',
                    defaultValue: '',
                    inputConfig: {
                      freeSolo: true,
                      onEnter: () => {
                        setPage(0)
                        fetchData()
                        getLabels()
                      },
                      onChange: val => {
                        labelPage.current = 0

                        if (!val) {
                          labelOptionsName.current = undefined
                          setSelectedLabel()
                          getLabels()
                          fetchData()
                        }
                      },
                      onInputChange: (_, value) => {
                        setLabelOptionsNameDebounce(value)
                      },
                      options: labelOptions,
                      isOptionEqualToValue: (option, value) => option.id === value.id,
                      placeholder: t('general.select_category'),
                    },
                  },
                  {
                    name: 'search',
                    defaultValue: '',
                    placeholder: t('general.insert_keyword'),
                    inputConfig: {
                      onChange: e => {
                        if (e.target) {
                          form.current.setValue?.('search', e.target.value)

                          if (isEmpty(e.target?.value)) {
                            setPage(0)
                            fetchData()
                            getLabels()
                          }
                        }
                      },
                      onSubmit: async () => {
                        setPage(0)
                        fetchData()
                        getLabels()
                      },
                    },
                  },
                ]}
                flexDirection="row"
                submitComponent={() => null}
                resetComponent={() => null}
              />
            </Grid>
          </Grid>

          <Grid alignItems="center" justifyContent="space-between" py={2}>
            <Grid item>
              <LabelSelectList
                isFullWidth
                hideEdit={!canModify}
                idKey="labelId"
                nameKey="name"
                useCountKey="useCnt"
                options={labelData?.map(d => ({ ...d, canDelete: d.name !== '未分類' })) || []}
                paging={labelPaging}
                page={labelPage.current}
                onPageChange={val => {
                  if (!isLabelsLoading) {
                    labelPage.current = val
                    getLabels()
                  }
                }}
                onDelete={setDeleteLabelData}
                selected={selectedLabel}
                onSelect={label => {
                  if (selectedLabel === label) {
                    setSelectedLabel()
                  } else {
                    setSelectedLabel(label)
                  }

                  setPage(0)
                }}
              />
            </Grid>
          </Grid>
          <Grid item>
            {!isEmptyData && !isLoading && (
              <Carousel
                fullHeightHover={false}
                autoPlay={false}
                indicators={false}
                height={'70vh'}
                sx={{
                  px: 5,
                  pt: 5,
                }}
                NavButton={({ next, prev, onClick }) => {
                  // Other logic
                  let disabled = true

                  if (next) {
                    if (
                      paging?.hasNext &&
                      !paging?.last &&
                      page !== Math.ceil((labelPaging?.total ?? 0) / LIST_PAGE_SIZE) - 1
                    ) {
                      disabled = false
                    }
                  }

                  if (prev) {
                    if (paging && !paging.first) {
                      disabled = false
                    }
                  }

                  return (
                    <IconButton
                      disabled={disabled}
                      color={'secondary_80'}
                      onClick={onClick}
                      style={disabled ? { backgroundColor: 'transparent' } : {}}
                    >
                      {next && <ArrowForwardIosIcon />}
                      {prev && <ArrowBackIosIcon />}
                    </IconButton>
                  )
                }}
                navButtonsWrapperProps={{
                  sx: { height: 'unset', top: '50%', transform: 'translateY(-50%)' },
                }}
                next={() => {
                  setPage(current => current + 1)
                }}
                prev={() => {
                  setPage(current => current - 1)
                }}
              >
                {Array.from({ length: paging?.totalPages ?? 0 }, i => i).map((_, pageIndex) => (
                  <Grid
                    className={globalLess.primaryScrollbar}
                    container
                    px={2}
                    key={`grid_${pageIndex + 1}`}
                    sx={{
                      height: '100%',
                    }}
                  >
                    <Grid spacing={5} item container sx={{ height: '100%', overflowY: 'auto' }}>
                      {data?.map(item => (
                        <Grid key={item.labelId} item xs={12} sm={2.4} md={2.4} lg={2.4} xl={2.4}>
                          <Typography variant="h6" marginBottom={2} display="flex">
                            <Tooltip
                              color={theme => theme.palette.secondary_80.main}
                              title={item.name}
                              placement="top-end"
                            >
                              <Typography
                                style={{
                                  fontWeight: 'bold',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {item.name}
                              </Typography>
                            </Tooltip>

                            <PermissionsController permission="courseVideo_modify">
                              <IconButton
                                size="small"
                                sx={{
                                  padding: 0,
                                  marginLeft: '8px',
                                  color: '#2D2D6C',
                                }}
                                onClick={() => {
                                  setEditLabelData(item)
                                }}
                              >
                                <Edit />
                              </IconButton>
                            </PermissionsController>

                            <PermissionsController permission="courseVideo_create">
                              <IconButton
                                size="small"
                                sx={{
                                  padding: 0,
                                  marginLeft: '2px',
                                  color: '#2D2D6C',
                                }}
                                onClick={() => {
                                  setEditCategoryVideoData(item)
                                }}
                              >
                                <AddIcon />
                              </IconButton>
                            </PermissionsController>
                          </Typography>
                          {item.courseTempList?.map(course => (
                            <Card
                              variant="outlined"
                              key={course?.templateId}
                              sx={{ marginBottom: '43px' }}
                            >
                              <CardMedia
                                component="img"
                                style={{ aspectRatio: '1.5', cursor: 'pointer' }}
                                image={course.coverImage}
                                alt={t('general.cover_image')}
                                onClick={() => {
                                  setVideoPreviewData(course)
                                }}
                              />
                              <CardContent sx={{ padding: '8px 8px 0' }}>
                                <Stack
                                  direction="row"
                                  flexWrap="nowrap"
                                  gap={1}
                                  alignItems="center"
                                  justifyContent="flex-end"
                                >
                                  <Tooltip title={course.name} placement="top-end">
                                    <Typography
                                      noWrap
                                      variant="body2"
                                      sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        width: '100%',
                                        textAlign: 'left',
                                      }}
                                    >
                                      {course.name}
                                    </Typography>
                                  </Tooltip>
                                  <Typography
                                    display="flex"
                                    justifyContent="center"
                                    variant="body2"
                                  >
                                    <ThumbUpAltIcon sx={{ color: '#F60' }} />
                                    {course.avgCourseRating ?? '-'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="#00000040"
                                    sx={{ textAlign: 'right' }}
                                  >
                                    {durationToStr(course.courseLength)}
                                  </Typography>
                                </Stack>
                              </CardContent>
                              <CardActions sx={{ justifyContent: 'flex-end' }} disableSpacing>
                                <PermissionsController permission="courseVideo_create">
                                  <IconButton
                                    size="small"
                                    sx={{ padding: 0, color: '#2D2D6C' }}
                                    onClick={() => {
                                      setEditVideoCategoryData(course)
                                    }}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                </PermissionsController>

                                <PermissionsController permission="courseVideo_modify">
                                  <IconButton
                                    size="small"
                                    sx={{ padding: 0, color: '#2D2D6C' }}
                                    onClick={() => {
                                      setPreviewCourseData(course)
                                    }}
                                  >
                                    <Edit />
                                  </IconButton>
                                </PermissionsController>

                                <PermissionsController permission="courseVideo_delete">
                                  <IconButton
                                    size="small"
                                    sx={{ padding: 0, color: '#2D2D6C' }}
                                    onClick={() => {
                                      setDeleteCourseLabelData({ label: item, course })
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </PermissionsController>
                              </CardActions>
                            </Card>
                          ))}
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                ))}
              </Carousel>
            )}
            {isEmptyData && (
              <Box
                sx={{
                  display: 'flex',
                  height: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" color="primary" fontWeight={'bold'}>
                  {isEmpty(form.current?.getValues?.().search)
                    ? t('errors.no_course')
                    : t('errors.no_course_name')}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Grid>

      <ConfirmDialog
        open={!!deleteLabelData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteLabelData()
        }}
        onSubmit={() => {
          deleteCourseTemplateLabel()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>{`${deleteLabelData?.name ?? ''}`}</Typography>
        </Stack>
      </ConfirmDialog>

      <ConfirmDialog
        open={!!deleteCourseLabelData}
        title={t('messages.confirm_delete')}
        onClose={() => {
          setDeleteCourseLabelData()
        }}
        onSubmit={() => {
          deleteCourseTemplateFromLabel()
        }}
        loading={deleteLoading}
      >
        <Stack alignItems="center">
          <Typography>
            {t('messages.confirm_delete_course_from_label', {
              category: deleteCourseLabelData?.label?.name,
              course: deleteCourseLabelData?.course?.name,
            })}
          </Typography>
        </Stack>
      </ConfirmDialog>

      {createLabelDialogOpen && (
        <CreateCourseTemplateCategoryDialog
          open={createLabelDialogOpen}
          onClose={() => {
            setCreateLabelDialogOpen(false)
          }}
          onComplete={() => {
            getLabels()
          }}
        />
      )}

      {!!editLabelData && (
        <EditCourseTemplateCategoryDialog
          data={editLabelData}
          open={!!editLabelData}
          onClose={() => {
            setEditLabelData()
          }}
          onComplete={() => {
            fetchData()
            getLabels()
          }}
        />
      )}

      {createCourseDialogOpen && (
        <CreateCourseDialog
          open={createCourseDialogOpen}
          onClose={() => {
            setCreateCourseDialogOpen(false)
          }}
          onComplete={() => {
            getLabels()
            fetchData()
          }}
        />
      )}

      {!!editVideoCategoryData && (
        <EditVideoCategoryDialog
          open={!!editVideoCategoryData}
          courseData={editVideoCategoryData}
          onClose={() => {
            setEditVideoCategoryData()
          }}
          onComplete={() => {
            getLabels()
            fetchData()
          }}
        />
      )}

      {!!editCategoryVideoData && (
        <EditCategoryVideoDialog
          open={!!editCategoryVideoData}
          categoryData={editCategoryVideoData}
          onClose={() => {
            setEditCategoryVideoData()
          }}
          onComplete={() => {
            getLabels()
            fetchData()
          }}
        />
      )}

      {previewCourseData && (
        <PreviewCourseVideoDialog
          courseData={previewCourseData}
          onComplete={() => {
            fetchData()
          }}
          onClose={() => {
            setPreviewCourseData()
          }}
        />
      )}

      {videoPreviewData && (
        <VideoPreviewDialog
          courseData={videoPreviewData}
          onClose={() => {
            setVideoPreviewData()
          }}
        />
      )}
    </Container>
  )
}

export default connect(mapStateToProps)(CourseManagement)
